<script setup lang="ts">
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { useGroupsStore } from "~/stores/groups"
import { useUserStore } from "~/stores/user"
import { usePlatform } from "~/composables/usePlatform"
import { useCallOverlay } from "~/composables/useCallOverlay"
import { Drawer, DrawerContent } from "@/components/ui/drawer"
import { useMobileDrawerCloseOnBack } from "~/composables/useMobileDrawerBackNav"
import { TooltipProvider } from "@/components/ui/tooltip"
import VoiceCallView from "~/components/voice-ui/Chat/VoiceCallView.vue"
import FloatingCallBubble from "~/components/voice-ui/Chat/FloatingCallBubble.vue"

// Global, route-independent host for the mobile call UI: it renders off `groupsStore`'s active
// voice channel (backed by the singleton `liveKitManager`, not by whatever page happens to be
// mounted — see `VOICE_CALLS_ARCHITECTURE.md`), so the call stays reachable via the floating
// bubble no matter where you navigate. Mounted once in `app.vue`.
const { isMobile } = usePlatform()
const groupsStore = useGroupsStore()
const userStore = useUserStore()
const { activeVoiceChannelId, groupDetails } = storeToRefs(groupsStore)
const { user } = storeToRefs(userStore)
const { isCallExpanded, minimizeCall } = useCallOverlay()

// Lets the Android hardware/gesture back button minimize the expanded call view back to the
// floating bubble (not disconnect the call) instead of leaving the app — see useMobileDrawerBackNav.ts.
useMobileDrawerCloseOnBack({ isOpen: isCallExpanded, enabled: isMobile })

const activeChannelInfo = computed(() => {
  const channelId = activeVoiceChannelId.value
  if (!channelId) return null
  const groupId = groupsStore.groupIdForChannel(channelId) ?? groupsStore.selectedGroupId
  const channel = groupId ? groupsStore.findChannel(groupId, channelId) : null
  const group = groupId ? groupDetails.value[groupId] : null
  return {
    groupId: groupId ?? "",
    groupName: group?.name ?? "Голосовой канал",
    avatarUrl: group?.avatar_url,
    channelId,
    channelName: channel?.name ?? "Голосовой канал",
  }
})

const userName = computed(() => user.value?.display_name || user.value?.username || "User")

function handleDisconnect() {
  groupsStore.disconnectChannel()
  groupsStore.selectChannel(null)
  minimizeCall()
}
</script>

<template>
  <template v-if="isMobile && activeChannelInfo">
    <Drawer v-model:open="isCallExpanded" :should-scale-background="false" class="">
      <!-- `h-dvh!`/`max-h-dvh!` (not `h-screen`): `100vh` on iOS is the *largest* viewport, taller
           than what's visible, which pushed the controls under the home indicator. The `!` also wins
           over the shared-height inline style DrawerContent sets while other Drawers are stacked
           (useDrawerStack.ts) — this is a full-screen view, not a sheet in that pile.
           `[&>div:first-child]:hidden` hides DrawerContent's drag-handle pill: this screen has its
           own header with a back arrow, like the chat screens. Swipe-down still minimizes. -->
      <DrawerContent
          class="mt-0 h-dvh! max-h-dvh! overflow-hidden rounded-none border-none p-0 [&>div:first-child]:hidden"
          style="padding-top: env(safe-area-inset-top)"
      >
        <TooltipProvider :delay-duration="0">
          <!-- `DrawerContent` lives outside `HomepageLayout` (mounted globally in app.vue), so it
               doesn't get that shell's own safe-area padding — apply it here directly. The
               drag-handle pill `DrawerContent` renders internally sits above this component's own
               content, so the top inset has to go on `DrawerContent` itself (not just the div
               below) or it pokes out above the safe area, under the notch/status bar. -->
          <div
              class="flex w-full h-full min-h-0 flex-col"
              style="padding-bottom: env(safe-area-inset-bottom); padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right)"
          >
            <VoiceCallView
                :key="activeChannelInfo.channelId"
                :group-name="activeChannelInfo.groupName"
                :channel-id="activeChannelInfo.channelId"
                :channel-name="activeChannelInfo.channelName"
                :user-name="userName"
                @disconnect="handleDisconnect"
            />
          </div>
        </TooltipProvider>
      </DrawerContent>
    </Drawer>

    <Transition name="bubble">
      <FloatingCallBubble
          v-if="!isCallExpanded"
          :channel-name="activeChannelInfo.channelName"
          :group-name="activeChannelInfo.groupName"
          :avatar-url="activeChannelInfo.avatarUrl"
          @expand="isCallExpanded = true"
      />
    </Transition>
  </template>
</template>
